import { FileUploadProgress } from '../types';

export type FileUploadState = {
  files: Record<string, File | null>;
  isUploading: boolean;
  uploadProgress: number;
  fileProgress: Record<string, FileUploadProgress>;
  error: string | null;
  uploadResults: string[];
};

/**
 * @description The action to perform on the file upload state
 */
export type FileUploadAction =
  | { type: 'ADD_FILE'; payload: { slotId: string; file: File } }
  | { type: 'REMOVE_FILE'; payload: string }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'CLEAR_ERROR' }
  | { type: 'START_UPLOAD' }
  | { type: 'SET_UPLOAD_PROGRESS'; payload: number }
  | { type: 'SET_FILE_PROGRESS'; payload: { slotId: string; progress: FileUploadProgress } }
  | { type: 'UPLOAD_SUCCESS'; payload: string[] }
  | { type: 'UPLOAD_FAILURE'; payload: string }
  | { type: 'RESET' };

export const initialFileUploadState: FileUploadState = {
  files: {},
  isUploading: false,
  uploadProgress: 0,
  fileProgress: {},
  error: null,
  uploadResults: [],
};

/**
 * @description Reducer for the file upload state
 */
export const fileUploadReducer = (
  state: FileUploadState,
  action: FileUploadAction,
): FileUploadState => {
  switch (action.type) {
    case 'ADD_FILE':
      return {
        ...state,
        files: {
          ...state.files,
          [action.payload.slotId]: action.payload.file,
        },
        error: null,
      };
    case 'REMOVE_FILE':
      const newFiles = { ...state.files };
      delete newFiles[action.payload];
      return {
        ...state,
        files: newFiles,
        error: null,
      };
    case 'SET_ERROR':
      return {
        ...state,
        error: action.payload,
      };
    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null,
      };
    case 'START_UPLOAD':
      return {
        ...state,
        isUploading: true,
        error: null,
        uploadProgress: 0,
        fileProgress: Object.keys(state.files).reduce((acc, slotId) => {
          acc[slotId] = {
            slotId,
            progress: 0,
            status: 'pending',
          };
          return acc;
        }, {} as Record<string, FileUploadProgress>),
      };
    case 'SET_UPLOAD_PROGRESS':
      return {
        ...state,
        uploadProgress: action.payload,
      };
    case 'SET_FILE_PROGRESS':
      return {
        ...state,
        fileProgress: {
          ...state.fileProgress,
          [action.payload.slotId]: action.payload.progress,
        },
      };
    case 'UPLOAD_SUCCESS':
      return {
        ...state,
        isUploading: false,
        uploadResults: action.payload,
        files: {}, // Clear files on success? Or keep them? Usually clear.
        uploadProgress: 0,
      };
    case 'UPLOAD_FAILURE':
      return {
        ...state,
        isUploading: false,
        error: action.payload,
        uploadProgress: 0,
      };
    case 'RESET':
      return {
        ...initialFileUploadState,
        fileProgress: {},
      };
    default:
      return state;
  }
};
